import { isDefined } from "@utils/helpers";
import { StoreSearchStore, StoreUrl } from "./storeSearch.types";

export const findStoreUrl = (
    storeUrls: Array<StoreUrl>,
    storeId: string
) => storeUrls.find(({ code }) => code === storeId)?.url ?? `./${storeId}`

const getStoreCoordinates = (store: woosmap.map.stores.StoreResponse): woosmap.map.LatLngLiteral => ({
    lat: store.geometry.coordinates[1],
    lng: store.geometry.coordinates[0]
});

const getDistancePromiseForStoreIndex = (
    distanceElementsPromise: Promise<woosmap.map.distance.DistanceMatrixElement[] | undefined> | undefined,
    index: number
) => distanceElementsPromise?.then((distanceElements) => {
    const distanceMatrixElement = distanceElements?.at(index);
    if (distanceMatrixElement?.status !== "OK") return undefined;

    return distanceMatrixElement.distance.value;
});

export const enrichStores = (
    stores: Array<woosmap.map.stores.StoreResponse>,
    storeUrls: Array<StoreUrl>,
    origin?: woosmap.map.LatLngLiteral,
    distanceService?: woosmap.map.DistanceService,
) => {
    const withDistance = isDefined(origin) && isDefined(distanceService);

    const distanceElementsPromise = withDistance
        ? distanceService.getDistanceMatrix({
            origins: [origin],
            destinations: stores.map(getStoreCoordinates),
            method: "distance",
            elements: "distance"
        })
            .then(({ rows }) => rows.at(0)?.elements)
        : undefined;

    return stores.map((store, index) => ({
        ...store,
        url: findStoreUrl(storeUrls, store.properties.user_properties.storeId),
        distancePromise: getDistancePromiseForStoreIndex(distanceElementsPromise, index)
    } satisfies StoreSearchStore))
};

export const toggleSearchParam = (handleUrl: () => void) => (param: string, value: string) => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(param, value)) {
        searchParams.delete(param, value);
    } else {
        searchParams.append(param, value);
    }

    history.pushState(null, "", "?" + searchParams);

    handleUrl();

    return searchParams;
};

export const setSearchParams = (handleUrl: () => void) => (
    params: Record<string, string | number>,
    paramsToDelete: Array<string>,
) => {
    const searchParams = new URLSearchParams(location.search);

    paramsToDelete.forEach(param => searchParams.delete(param));

    Object.entries(params).forEach(([key, value]) =>
        searchParams.set(key, value.toString()),
    );

    history.pushState(null, "", "?" + searchParams);
    handleUrl();

    return searchParams;
};
